import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { KEYWORD_INPUT_ID } from '../../constants';
import { useSearchContext } from '../../context/Search';
import { getNestedValue } from '../../common/utils/helpers';
import debounce from '../../common/utils/debounce';

const useKeywordInputLogic = () => {
  const {
    categorySuggest: { fetchCategorySuggestions, setCategorySuggestions },
    focusedInput: [, setFocusedInput],
    initialSearch,
    modal: [modal],
    profileSuggest: { fetchProfileSuggestions, setProfileSuggestions },
    recentSearches: { lastSearch },
    selectedKeyword: [selectedKeyword, setSelectedKeyword],
    selectedSearchArea: [selectedSearchArea],
  } = useSearchContext();
  const { formState, getFieldState } = useFormContext();
  const modalKeyword = getNestedValue(modal, 'data', 'selectedKeyword');

  /**
   * On change, reset suggestions if the field is empty, otherwise fetch category and profile suggestions
   * @param {Object} event The event object
   * @returns {void}
   */
  const handleChange = event => {
    const inputValue = event.target.value;
    debounce(() => {
      if (!inputValue) {
        setProfileSuggestions();
        setCategorySuggestions();
      } else {
        fetchCategorySuggestions({
          query: inputValue,
          debounceFetch: false,
        });
        fetchProfileSuggestions({
          query: inputValue,
          debounceFetch: false,
          ...(selectedSearchArea ? { searchArea: selectedSearchArea } : {}),
        });
      }
    });
  };

  /**
   * On focus, set this field as the focused input
   * @returns {void}
   */
  const handleFocus = () => {
    setFocusedInput(KEYWORD_INPUT_ID);
  };

  /**
   * Attempt to set the initial value based on the following priority:
   * - Selected keyword passed in from modal
   * - The most recent search's keyword
   */
  useEffect(() => {
    const { isDirty } = getFieldState(KEYWORD_INPUT_ID, formState);
    if (modalKeyword) {
      setSelectedKeyword(modalKeyword);
    } else if (!isDirty) {
      if (initialSearch && initialSearch.keyword) {
        setSelectedKeyword(initialSearch.keyword);
      } else if (!selectedKeyword && lastSearch) {
        setSelectedKeyword(lastSearch.keyword);
      }
    }
  }, [initialSearch, lastSearch, modalKeyword]);

  return { handleChange, handleFocus };
};

export default useKeywordInputLogic;
